import React from 'react';

import {
	AdminPanelSettings,
	Architecture,
	Air,
	Assessment,
	AppRegistration,
	Article,
	Chat,
	Cloud,
	HexagonOutlined,
	Home,
	InsertDriveFile,
	Layers,
	Mail,
	MenuBook,
	People,
	Security,
	ViewQuilt,
} from '@mui/icons-material';

import AgentIcon from '../common/components/AgentIcon';
import { UserClaims } from '../common/UserClaims';
import { Permissions } from '../common/Permissions';
import { IconServiceConnection } from '../views/FlowDesigner/Components/Icons';

export type MenuSectionDesc = {
	title: string;
	items: MenuItemDesc[];
};

export type MenuItemDesc = {
	action?: 'architect';
	title: string;
	icon: React.ReactElement;
	path?: string;
	requiredAuth?: boolean;
	hiddenWhenAuthenticated?: boolean;
	requiredClaim?: string;
	requiredPermission?: string;
	requiredPermissions?: string[];
	requiredAnyPermissions?: string[];
	onClick?: () => void;
};

export type LeftMenuProps = {
	open: boolean;
};

export const menuItems: MenuSectionDesc[] = [
	{
		title: 'Home',
		items: [
			{
				title: 'Home',
				icon: <Home />,
				path: 'home',
			},
			{
				title: 'Register',
				icon: <AppRegistration />,
				path: 'register',
				hiddenWhenAuthenticated: true,
			},
		],
	},
	{
		title: 'Planning',
		items: [
			{
				title: 'Projects',
				icon: <MenuBook />,
				path: 'planning/projects',
				requiredAuth: true,
				requiredPermission: Permissions.ProjectRead,
			},
		],
	},
	{
		title: 'Implementation',
		items: [
			{
				title: 'Architect', icon: <Architecture />, action: 'architect', requiredAuth: true,
				requiredPermissions: [Permissions.ConversationRead, Permissions.ConversationUpdate],
			},
			{
				title: 'Apps',
				icon: <ViewQuilt />,
				path: 'app',
				requiredAuth: true,
				requiredPermission: Permissions.AppRead,
			},
			{
				title: 'Services',
				icon: <HexagonOutlined />,
				path: 'service',
				requiredAuth: true,
				requiredPermission: Permissions.ServiceRead,
			},
			{
				title: 'Orchestration',
				icon: <Air />,
				path: 'flow',
				requiredAuth: true,
				requiredPermission: Permissions.FlowRead,
			},
			{
				title: 'Events',
				icon: <Mail />,
				path: 'integration-events',
				requiredAuth: true,
				requiredPermission: Permissions.IntegrationEventRead,
			},
		],
	},
	{
		title: 'Content',
		items: [
			{
				title: 'Resources', icon: <MenuBook />, path: 'resources', requiredAuth: true,
				requiredAnyPermissions: [Permissions.ServiceRead, Permissions.AppRead],
			},
			{
				title: 'Templates',
				icon: <Article />,
				path: 'template',
				requiredAuth: true,
				requiredPermission: Permissions.ContentRead,
			},
			{
				title: 'Files',
				icon: <InsertDriveFile />,
				path: 'file',
				requiredAuth: true,
				requiredPermission: Permissions.FileRead,
			},
			{
				title: 'Conversations',
				icon: <Chat />,
				path: 'conversation',
				requiredAuth: true,
				requiredPermission: Permissions.ConversationRead,
			},
			{
				title: 'Permissions',
				icon: <Security />,
				path: 'permissions',
				requiredAuth: true,
				requiredPermissions: [Permissions.PermissionRead, Permissions.RoleRead],
			},
		],
	},
	{
		title: 'Infrastructure',
		items: [
			{
				title: 'Service Connections',
				icon: <IconServiceConnection />,
				path: 'service-connections',
				requiredAuth: true,
				requiredPermission: Permissions.ServiceConnectionRead,
			},
			{
				title: 'Environments',
				icon: <Cloud />,
				path: 'environments',
				requiredAuth: true,
				requiredPermission: Permissions.EnvironmentRead,
			},
			{
				title: 'Change Sets',
				icon: <Layers />,
				path: 'changesets',
				requiredAuth: true,
				requiredPermission: Permissions.ChangeSetRead,
			},
		],
	},
	{
		title: 'Security',
		items: [
			{
				title: 'Audit',
				icon: <Assessment />,
				path: 'audit',
				requiredAuth: true,
				requiredPermission: Permissions.AuditRead,
			},
			{
				title: 'Agents',
				icon: <AgentIcon />,
				path: 'agents',
				requiredAuth: true,
				requiredClaim: UserClaims.CanChangeTenantId,
				requiredPermission: Permissions.AgentRead,
			},
			{
				title: 'Users',
				icon: <People />,
				path: 'users',
				requiredAuth: true,
				requiredClaim: UserClaims.CanChangeTenantId,
				requiredPermission: Permissions.UserRead,
			},
			{
				title: 'Permissions',
				icon: <Security />,
				path: 'permissions',
				requiredAuth: true,
				requiredPermissions: [Permissions.PermissionRead, Permissions.RoleRead],
			},
		],
	},
	{
		title: 'Admin',
		items: [
			{
				title: 'Tenants',
				icon: <AdminPanelSettings />,
				path: 'tenants',
				requiredAuth: true,
				requiredClaim: UserClaims.CanChangeTenantId,
			},
		],
	},
];

